import { Alert, Button, Card, Link, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import IPrincipalData from './data/IPrincipalData'
import { TAppRoles, getUserInfoAsync, roleToPath, userInRole } from './utils/PrinicpalUtils'

type TAppStatus =
  | 'checking'
  | 'login'
  | 'loginTrouble'
  | 'unauthorized'
  | 'roleSelection'
  | 'error'

type TDiagnostics = {
  capturedAt: string
  status: string
  href: string
  userAgent: string
  online: boolean
  loginAttemptedAt?: string | null
  rawAuthMe?: unknown
  principal?: unknown
  roles?: unknown
  error?: unknown
}

const LOGIN_ATTEMPT_KEY = 'wizard-login-attempted-at'

const centeredSx = {
  m: 0,
  p: 2,
  position: 'absolute',
  top: '50%',
  left: '50%',
  msTransform: 'translate(-50%, -50%)',
  transform: 'translate(-50%, -50%)',
  minWidth: 320,
  maxWidth: 720
} as const

function safeJson(value: unknown) {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

function getPrincipalName(principal?: IPrincipalData) {
  const p = principal as IPrincipalData & {
    userDetails?: string
    identityProvider?: string
  }

  return p?.userDetails ?? p?.userId ?? 'unknown account'
}

async function getRawAuthMe() {
  const response = await fetch('/.auth/me', {
    credentials: 'include',
    cache: 'no-store'
  })

  const text = await response.text()

  let body: unknown = text

  try {
    body = JSON.parse(text)
  } catch {
    // Keep raw text
  }

  return {
    status: response.status,
    ok: response.ok,
    body
  }
}

function buildDiagnostics(
  status: string,
  loginAttemptedAt?: string | null,
  rawAuthMe?: unknown,
  principal?: unknown,
  roles?: unknown,
  error?: unknown
): TDiagnostics {
  return {
    capturedAt: new Date().toISOString(),
    status,
    href: window.location.href,
    userAgent: navigator.userAgent,
    online: navigator.onLine,
    loginAttemptedAt,
    rawAuthMe,
    principal,
    roles,
    error
  }
}

function DiagnosticsCard({ diagnostics }: { diagnostics?: TDiagnostics }) {
  const [copied, setCopied] = useState(false)

  if (!diagnostics) {
    return null
  }

  const text = safeJson(diagnostics)

  const copyDiagnostics = async () => {
    await navigator.clipboard.writeText(text)
    setCopied(true)
  }

  return (
    <Card sx={{ mt: 2, p: 1, maxWidth: 700 }}>
      <Stack spacing={1}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
          Diagnostics
        </Typography>

        <Button size="small" variant="outlined" onClick={copyDiagnostics}>
          {copied ? 'Copied' : 'Copy diagnostics'}
        </Button>

        <Typography
          component="pre"
          sx={{
            m: 0,
            p: 1,
            fontSize: 11,
            whiteSpace: 'pre-wrap',
            maxHeight: 320,
            overflow: 'auto',
            backgroundColor: '#f5f5f5'
          }}
        >
          {text}
        </Typography>
      </Stack>
    </Card>
  )
}

export default function App() {
  const [principal, setPrincipal] = useState<IPrincipalData>()
  const [roles, setRoles] = useState<TAppRoles[]>([])
  const [status, setStatus] = useState<TAppStatus>('checking')
  const [diagnostics, setDiagnostics] = useState<TDiagnostics>()

  const navigate = useNavigate()

  const appRootUrl = `${window.location.origin}/`
  const loginUrl = `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(appRootUrl)}`
  const appLogoutUrl = `/.auth/logout?post_logout_redirect_uri=${encodeURIComponent(appRootUrl)}`
  const microsoftLogoutUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(appRootUrl)}`

  const markLoginAttempt = () => {
    sessionStorage.setItem(LOGIN_ATTEMPT_KEY, new Date().toISOString())
  }

  useEffect(() => {
    let cancelled = false

    const fetchActive = async () => {
      const loginAttemptedAt = sessionStorage.getItem(LOGIN_ATTEMPT_KEY)

      setStatus('checking')
      setRoles([])
      setDiagnostics(undefined)

      let rawAuthMe: unknown

      try {
        rawAuthMe = await getRawAuthMe()

        const currentPrincipal = await getUserInfoAsync()

        if (cancelled) {
          return
        }

        setPrincipal(currentPrincipal)

        if (!currentPrincipal?.userId) {
          if (loginAttemptedAt) {
            setDiagnostics(
              buildDiagnostics(
                'Login was attempted, but the app still sees no authenticated user',
                loginAttemptedAt,
                rawAuthMe,
                currentPrincipal
              )
            )
            setStatus('loginTrouble')
            return
          }

          setStatus('login')
          return
        }

        const currentRoles = await userInRole(currentPrincipal)

        if (cancelled) {
          return
        }

        if (currentRoles.length === 0) {
          setDiagnostics(
            buildDiagnostics(
              'Authenticated Microsoft account has no Wizard roles',
              loginAttemptedAt,
              rawAuthMe,
              currentPrincipal,
              currentRoles
            )
          )
          setStatus('unauthorized')
          return
        }

        sessionStorage.removeItem(LOGIN_ATTEMPT_KEY)

        if (currentRoles.length === 1) {
          navigate(roleToPath(currentRoles[0]), { replace: true })
          return
        }

        setRoles(currentRoles)
        setStatus('roleSelection')
      } catch (error) {
        if (cancelled) {
          return
        }

        setDiagnostics(
          buildDiagnostics(
            'Login check failed with an exception',
            loginAttemptedAt,
            rawAuthMe,
            principal,
            roles,
            error instanceof Error
              ? {
                  name: error.name,
                  message: error.message,
                  stack: error.stack
                }
              : error
          )
        )
        setStatus('error')
      }
    }

    fetchActive()

    return () => {
      cancelled = true
    }
  }, [navigate]) // eslint-disable-line react-hooks/exhaustive-deps

  if (status === 'checking') {
    return (
      <Card sx={centeredSx}>
        <Typography>Checking login status...</Typography>
      </Card>
    )
  }

  if (status === 'error') {
    return (
      <Card sx={centeredSx}>
        <Stack spacing={2}>
          <Alert severity="error">
            Wizard could not check the current login status.
          </Alert>

          <Button variant="contained" href={loginUrl} onClick={markLoginAttempt}>
            Try logging in again
          </Button>

          <Link href={appLogoutUrl}>Log out of Wizard</Link>
          <Link href={microsoftLogoutUrl}>Log out of Microsoft account</Link>

          <DiagnosticsCard diagnostics={diagnostics} />
        </Stack>
      </Card>
    )
  }

  if (status === 'unauthorized') {
    return (
      <Card sx={centeredSx}>
        <Stack spacing={2}>
          <Alert severity="warning">
            You are signed in as {getPrincipalName(principal)}, but this account is not registered for Wizard.
          </Alert>

          <Typography>
            Please log out and sign in with the Microsoft account that has Wizard access.
          </Typography>

          <Button variant="contained" href={appLogoutUrl}>
            Log out of Wizard
          </Button>

          <Link href={microsoftLogoutUrl}>Log out of Microsoft account</Link>

          <DiagnosticsCard diagnostics={diagnostics} />
        </Stack>
      </Card>
    )
  }

  if (status === 'loginTrouble') {
    return (
      <Card sx={centeredSx}>
        <Stack spacing={2}>
          <Alert severity="warning">
            Login was attempted, but Wizard still does not see an authenticated user.
          </Alert>

          <Typography>
            Please try logging in again. If the problem continues, log out of Microsoft and then try again.
          </Typography>

          <Button variant="contained" href={loginUrl} onClick={markLoginAttempt}>
            Try logging in again
          </Button>

          <Link href={appLogoutUrl}>Log out of Wizard</Link>
          <Link href={microsoftLogoutUrl}>Log out of Microsoft account</Link>

          <DiagnosticsCard diagnostics={diagnostics} />
        </Stack>
      </Card>
    )
  }

  if (status === 'roleSelection') {
    return (
      <Card sx={centeredSx}>
        <Typography sx={{ fontSize: 18 }} color="text.secondary" gutterBottom>
          Please select the role
        </Typography>

        <Stack>
          {roles.map(r => (
            <Link key={r} href={roleToPath(r)}>
              <Typography sx={{ fontSize: 16 }}>{r}</Typography>
            </Link>
          ))}
        </Stack>

        <Typography sx={{ mt: '10px', fontSize: 18 }} color="text.secondary" gutterBottom>
          or
        </Typography>

        <Link href={appLogoutUrl}>log out</Link>
      </Card>
    )
  }

  return (
    <>
      <Link href={microsoftLogoutUrl} sx={{ position: 'absolute', top: 0, right: 8 }}>
        Log out current Microsoft account
      </Link>

      <Button
        sx={{
          m: 0,
          position: 'absolute',
          top: '50%',
          left: '50%',
          msTransform: 'translate(-50%, -50%)',
          transform: 'translate(-50%, -50%)'
        }}
        variant="contained"
        href={loginUrl}
        onClick={markLoginAttempt}
      >
        Please log in
      </Button>
    </>
  )
}